import { createContext, useState, useContext, FC, ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useURLContext } from "./URLContext";
import showToast from "../hooks/useCustomToast";
import { UserProto } from "build-ts/interface";
import { getLoginUrl, getLogoutUrl, getSessionInfoUrl } from "../services/newtonProperties";

/* structure of user data*/
interface UserData {
  isLoggedIn: boolean;
  user: UserProto | undefined;
  setUser: React.Dispatch<React.SetStateAction<UserProto | undefined>>;
  isAdmin: boolean;
  username: string | undefined;
  setUsername: React.Dispatch<React.SetStateAction<string>>;
  userId: string | undefined;
  defaultTimeZone: string;
  quarterStartMonth: string;
  login: (enteredUsername: string, enteredPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  errorMsg: string;
  defaultAnalystId: string;
  setDefaultAnalystId: React.Dispatch<React.SetStateAction<string>>;
  tsBaseUrl: string;
  tsLoginUrl: string;
  tsTokenUrl: string;
  tsTokenValiditySecs: number;
  tsLoginUsername: string;
  tsLoginPassword: string;
  isTSInitialized: boolean;
  setIsTSInitialized: React.Dispatch<React.SetStateAction<boolean>>;
}

/* context for managing user data*/
const UserContext = createContext<UserData | undefined>(undefined);

/* Custom hook to access user data from the context */
export const useUser = (): UserData => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

/* props for the UserProvider component */
interface UserProviderProps {
  children: ReactNode;
}

/* Provider component for managing user data*/
export const UserProvider: FC<UserProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { preLoginUrl, setPreLoginUrl, clearPreLoginUrl } = useURLContext();

  /* State variables for managing user data*/
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<UserProto | undefined>(undefined);
  const [username, setUsername] = useState("");
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [defaultTimeZone, setDefaultTimeZone] = useState<string>("");
  const [quarterStartMonth, setQuarterStartMonth] = useState<string>("");
  const [defaultAnalystId, setDefaultAnalystId] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  /* ThoughtSpot login credentials*/
  const [tsBaseUrl, setTsBaseUrl] = useState<string>("");
  const [tsLoginUrl, setTsLoginUrl] = useState<string>("");
  const [tsTokenUrl, setTsTokenUrl] = useState<string>("");
  const [tsTokenValiditySecs, setTsTokenValiditySecs] = useState<number>(0);
  const [tsLoginUsername, setTsLoginUsername] = useState<string>("");
  const [tsLoginPassword, setTsLoginPassword] = useState<string>("");
  const [isTSInitialized, setIsTSInitialized] = useState<boolean>(false);

  /* Function to handle user login*/
  const login = async (enteredUsername: string, enteredPassword: string) => {
    try {
      const formData = new FormData();
      formData.append("username", enteredUsername);
      formData.append("password", enteredPassword);

      const response = await fetch(getLoginUrl(), {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const responseData = await response.json();
      const user = UserProto.fromJSON(responseData);
      const clientObject = JSON.parse(user?.client_object_json || "{}");

      if (response.status === 200) {
        setIsLoggedIn(true);
        setUser(user);
        setUsername(user?.header?.name || "");
        setUserId(user?.header?.id || "");
        setIsAdmin(user?.is_admin || false);
        setDefaultAnalystId(clientObject?.defaultAnalystId || "");
        /* ThoughtSpot login credentials*/
        setTsBaseUrl(responseData?.tsLogin?.baseUrl || "");
        setTsLoginUrl(responseData?.tsLogin?.loginUrl || "");
        setTsTokenUrl(responseData?.tsLogin?.tokenUrl || "");
        setTsTokenValiditySecs(responseData?.tsLogin?.tokenValiditySeconds || 0);
        setTsLoginUsername(responseData?.tsLogin?.username || "");
        setTsLoginPassword(responseData?.tsLogin?.password || "");
        const redirectUrl = preLoginUrl || "/";
        clearPreLoginUrl();
        // Replace the current page with the redirect URL.
        navigate(redirectUrl, { replace: true });
        setIsAdmin(responseData.isAdmin);
        setDefaultTimeZone(responseData.timezone);
        setQuarterStartMonth(responseData.quarterStartMonth);
      } else {
        setIsLoggedIn(false);
        setUsername("");
        setUserId(undefined);
        if (responseData?.errorMsg) {
          setErrorMsg(responseData?.errorMsg);
        }
      }
    } catch (error) {
      console.error("Error logging in:", error);
    }
  };

  /* Function to handle user logout*/
  const logout = async () => {
    const response = await fetch(getLogoutUrl(), {
      method: "POST",
      credentials: "include",
    });

    if (response.status === 204) {
      setIsLoggedIn(false);
      setUsername("");
      setUserId(undefined);
      navigate("/login");
      setIsAdmin(false);
      setDefaultTimeZone("");
      setQuarterStartMonth("");
    } else {
      showToast("error in logout", "", "error");
    }
  };

  /* Function to reload user session*/
  const reload = async () => {
    try {
      const response = await fetch(getSessionInfoUrl(), {
        method: "GET",
        credentials: "include",
      });
      const responseData = await response.json();
      const user = UserProto.fromJSON(responseData);
      const clientObject = JSON.parse(user?.client_object_json || "{}");
      if (response.ok) {
        setIsLoggedIn(true);
        setUser(user);
        setUsername(user?.header?.name || "");
        setUserId(user?.header?.id || "");
        setIsAdmin(user?.is_admin || false);
        setDefaultTimeZone(responseData.timezone);
        setQuarterStartMonth(responseData.quarterStartMonth);
        setDefaultAnalystId(clientObject?.defaultAnalystId || "");
        /* ThoughtSpot login credentials*/
        setTsBaseUrl(responseData?.tsLogin?.baseUrl || "");
        setTsLoginUrl(responseData?.tsLogin?.loginUrl || "");
        setTsTokenUrl(responseData?.tsLogin?.tokenUrl || "");
        setTsTokenValiditySecs(responseData?.tsLogin?.tokenValiditySeconds || 0);
        setTsLoginUsername(responseData?.tsLogin?.username || "");
        setTsLoginPassword(responseData?.tsLogin?.password || "");
      } else {
        const fullPath = location.pathname + location.search;
        setPreLoginUrl(fullPath);
        showToast("Session expired! please login.", "", "warning");
      }
    } catch (error) {
      console.error("Error while reloading session:", error);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true); // Set isLoading to true before fetching data
      await reload(); // Call the reload function
      setIsLoading(false);
    };

    fetchData(); // Call the fetchData function when the component mounts
  }, []);

  /* Provide user data through context*/
  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        user,
        setUser,
        isAdmin,
        username,
        setUsername,
        userId,
        defaultTimeZone,
        quarterStartMonth,
        login,
        logout,
        reload,
        isLoading,
        setIsLoading,
        errorMsg,
        defaultAnalystId,
        setDefaultAnalystId,
        tsBaseUrl,
        tsLoginUrl,
        tsTokenUrl,
        tsTokenValiditySecs,
        tsLoginUsername,
        tsLoginPassword,
        isTSInitialized,
        setIsTSInitialized,
      }}
    >
      {isLoading ? (
        <div></div> // Render loading indicator while data is being fetched
      ) : (
        children
      )}
    </UserContext.Provider>
  );
};
